import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Pill } from "@/components/site/Reveal";
import ShaderBackground from "@/components/site/ShaderBackground";
import { lastRoom } from "@/lib/room";

// "Client sign in" in the site's menu: for someone who knows their address but
// not where their page is. We send a link to every page that address can open.
export default function Client() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  // This browser was on a client page before: offer the way straight back.
  const [last] = useState(() => lastRoom());
  const send = async (e) => {
    e.preventDefault();
    if (state === "busy" || !email.trim()) return;
    setState("busy");
    setError("");
    try {
      const response = await fetch("/api/room/find", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "");
      setState("sent");
    } catch (err) {
      setError(err.message || "That did not work. Try again in a moment.");
      setState("idle");
    }
  };
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden bg-black px-5 pb-16 pt-28 text-white"
      data-testid="client-page"
    >
      <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto w-full max-w-[1400px]">
        <Pill white>
          <Lock size={11} className="mr-1.5" /> Clients
        </Pill>
        <h1 className="nm-title mt-6 text-[48px] leading-[0.98] sm:text-[84px]">
          Your Audit.
        </h1>
        {state === "sent" ? (
          <div className="mt-6 max-w-[520px]" data-testid="client-sent">
            <p className="text-[19px] leading-[1.35] sm:text-[22px]">
              Check your inbox.
            </p>
            <p className="mt-3 text-[17px] leading-[1.45] text-white/65">
              If {email} has an Audit page with us, a sign-in link is on its
              way. It works once, for 15 minutes.
            </p>
            <button
              type="button"
              onClick={() => setState("idle")}
              className="font-sans nm-link mt-6 text-[14px] text-white/60"
            >
              Use another address
            </button>
          </div>
        ) : (
          <>
            <p className="mt-6 max-w-[520px] text-[19px] leading-[1.35] text-white/65 sm:text-[22px]">
              Enter your work email and we send you a link that signs you in to
              your page.
            </p>
            <form
              onSubmit={send}
              className="mt-10 flex max-w-[520px] flex-col gap-2 sm:flex-row"
            >
              <input
                aria-label="Your work email"
                placeholder="you@company.com"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="client-email"
                className="h-12 w-full min-w-0 shrink-0 rounded-full bg-white/10 px-6 text-[16px] text-white outline-none ring-1 ring-white/15 placeholder:text-white/30 focus:ring-white/60 sm:w-auto sm:flex-1"
              />
              <button
                type="submit"
                disabled={state === "busy" || !email.trim()}
                data-testid="client-send"
                className="h-12 shrink-0 rounded-full bg-white px-7 text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-60"
              >
                {state === "busy" ? "Sending…" : "Email me a link"}
              </button>
            </form>
            {last && (
              <p className="mt-5 text-[14px] text-white/55">
                This device has been on a client page before.{" "}
                <Link
                  to={`/a/${last}`}
                  className="nm-link text-white"
                  data-testid="client-last"
                >
                  Open it
                </Link>
              </p>
            )}
          </>
        )}
        {error && (
          <p role="alert" className="mt-4 text-[14px] text-red-300">
            {error}
          </p>
        )}
        <p className="mt-10 max-w-[460px] text-[13px] leading-[1.45] text-white/40">
          Not a client yet?{" "}
          <Link to="/scorecard" className="nm-link text-white/70">
            Start with the check
          </Link>
          . Trouble getting in? Write to hello@nomus.work and a person lets you
          in.
        </p>
      </div>
    </section>
  );
}
